import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

// ─── Types ──────────────────────────────────────────────────────────

export interface SACompany {
  id: string;
  name: string;
  industries: string[];
  createdAt: string;
  userCount: number;
  leadCount: number;
  eventCount: number;
  referredBy: { id: string; name: string } | null;
}

export interface SASalesRep {
  id: string;
  name: string;
  email: string;
  slug: string;
  isActive: boolean;
  createdAt: string;
  referralCount: number;
}

export interface SADeletePreview {
  companyName: string;
  userCount: number;
  leadCount: number;
  eventCount: number;
  invitationCount: number;
  hasSuperAdmin: boolean;
  canDelete: boolean;
}

export interface SACompanyDetail extends Omit<SACompany, 'userCount' | 'eventCount'> {
  users: SAUser[];
  events: SAEvent[];
}

export interface SAUser {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  role: string;
  isSuperAdmin: boolean;
  isDisabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  companyId: string;
  companyName: string;
  leadCount: number;
}

export interface SAEvent {
  id: string;
  name: string;
  slug: string | null;
  checksUsed: number;
  checksLimit: number;
  isActive: boolean;
  startDate: string;
  endDate: string;
  location: string;
  companyId: string;
  companyName: string;
  leadCount: number;
}

export interface SAAnalytics {
  companyCount: number;
  userCount: number;
  leadCount: number;
  eventCount: number;
  enrichmentJobCount: number;
  totalCostUsd: number;
}

interface PaginatedResponse<T> {
  total: number;
  page: number;
  totalPages: number;
  [key: string]: unknown;
  // items under a specific key
}

// ─── Queries ────────────────────────────────────────────────────────

export interface SortState {
  field: string;
  order: 'asc' | 'desc';
}

export function useSACompanies(page: number, search: string, sort?: SortState) {
  const params = new URLSearchParams({ page: String(page), limit: '25', search });
  if (sort) {
    params.set('sortField', sort.field);
    params.set('sortOrder', sort.order);
  }
  return useQuery({
    queryKey: ['sa-companies', page, search, sort?.field, sort?.order],
    queryFn: () =>
      apiFetch<{ companies: SACompany[]; total: number; page: number; totalPages: number }>(
        `/api/super-admin/companies?${params}`,
      ),
    staleTime: 30_000,
  });
}

export function useSACompanyDetail(companyId: string | null) {
  return useQuery({
    queryKey: ['sa-company', companyId],
    queryFn: () => apiFetch<SACompanyDetail>(`/api/super-admin/companies/${companyId}`),
    enabled: !!companyId,
    staleTime: 30_000,
  });
}

export function useSAUsers(page: number, search: string, companyId?: string, sort?: SortState) {
  const params = new URLSearchParams({ page: String(page), limit: '25', search });
  if (companyId) params.set('companyId', companyId);
  if (sort) {
    params.set('sortField', sort.field);
    params.set('sortOrder', sort.order);
  }
  return useQuery({
    queryKey: ['sa-users', page, search, companyId, sort?.field, sort?.order],
    queryFn: () =>
      apiFetch<{ users: SAUser[]; total: number; page: number; totalPages: number }>(
        `/api/super-admin/users?${params}`,
      ),
    staleTime: 30_000,
  });
}

export function useSAAnalytics() {
  return useQuery({
    queryKey: ['sa-analytics'],
    queryFn: () => apiFetch<SAAnalytics>('/api/super-admin/analytics'),
    staleTime: 60_000,
  });
}

export function useSAEventAnalytics(companyId?: string) {
  const params = companyId ? `?companyId=${companyId}` : '';
  return useQuery({
    queryKey: ['sa-event-analytics', companyId],
    queryFn: () =>
      apiFetch<{ events: SAEvent[] }>(`/api/super-admin/analytics/events${params}`),
    staleTime: 30_000,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────

export function useSetEventQuota() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, checksLimit }: { eventId: string; checksLimit: number }) =>
      apiFetch(`/api/super-admin/events/${eventId}/quota`, {
        method: 'PATCH',
        body: JSON.stringify({ checksLimit }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sa-event-analytics'] });
      qc.invalidateQueries({ queryKey: ['sa-company'] });
    },
  });
}

export function useBulkSetEventQuota() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, checksLimit }: { companyId: string; checksLimit: number }) =>
      apiFetch<{ ok: boolean; eventsUpdated: number; checksLimit: number }>(
        `/api/super-admin/companies/${companyId}/events/quota`,
        { method: 'PATCH', body: JSON.stringify({ checksLimit }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sa-event-analytics'] });
      qc.invalidateQueries({ queryKey: ['sa-company'] });
      qc.invalidateQueries({ queryKey: ['sa-companies'] });
    },
  });
}

export function useToggleUserDisabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, isDisabled }: { userId: string; isDisabled: boolean }) =>
      apiFetch(`/api/super-admin/users/${userId}/disable`, {
        method: 'PATCH',
        body: JSON.stringify({ isDisabled }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sa-users'] });
      qc.invalidateQueries({ queryKey: ['sa-company'] });
    },
  });
}

export function useTriggerPasswordReset() {
  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ ok: boolean; email: string }>(`/api/super-admin/users/${userId}/reset-password`, {
        method: 'POST',
      }),
  });
}

// ─── Sales Rep queries & mutations ─────────────────────────────────

export function useSASalesReps() {
  return useQuery({
    queryKey: ['sa-sales-reps'],
    queryFn: () =>
      apiFetch<{ salesReps: SASalesRep[] }>('/api/super-admin/sales-reps'),
    staleTime: 30_000,
  });
}

export function useCreateSalesRep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; email: string; slug: string }) =>
      apiFetch('/api/super-admin/sales-reps', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sa-sales-reps'] });
    },
  });
}

export function useUpdateSalesRep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; email?: string; slug?: string; isActive?: boolean }) =>
      apiFetch(`/api/super-admin/sales-reps/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sa-sales-reps'] });
    },
  });
}

// ─── Delete organization ───────────────────────────────────────────

export function useSADeletePreview(companyId: string | null) {
  return useQuery({
    queryKey: ['sa-delete-preview', companyId],
    queryFn: () =>
      apiFetch<SADeletePreview>(`/api/super-admin/companies/${companyId}/delete-preview`),
    enabled: !!companyId,
  });
}

export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string) =>
      apiFetch(`/api/super-admin/companies/${companyId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sa-companies'] });
      qc.invalidateQueries({ queryKey: ['sa-analytics'] });
    },
  });
}
